import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ProductFilters.css";

/* ═══════════════════════════════════════════════════════════════════════════
   PRODUCT FILTERS

   Gender / Garment type / Colour / Size / Price, derived entirely from the
   products that were handed in. Nothing about the catalogue is written down
   here: the owner adds garment types and colours from the admin panel, and a
   hardcoded list would silently go stale the day he does. A type he does not
   currently stock simply never appears.

   The authoritative source for a product's sizes and colours is its `stock`
   array — `attributes.color` on the live data is free text ("red, white",
   "ada") and cannot be trusted as a facet.

   Two fields are OPTIONAL and absent from every product uploaded so far:

     productType : String            e.g. "Hoodie"  — the garment type facet
     gender      : men|women|unisex  — the gender facet

   Both facets are gated on real data (see deriveFacets). On today's catalogue,
   where neither field is set, neither facet is produced and the panel is
   exactly the Colour / Size / Price panel it is today.

   Every pure function below is defensive and every one of them is called from
   inside a try/catch by the page. If anything here throws, the page falls back
   to the unfiltered list: products must never be able to disappear because a
   filter misbehaved.
   ═══════════════════════════════════════════════════════════════════════════ */

export const EMPTY_SELECTION = {
  genders: [],
  types: [],
  colors: [],
  sizes: [],
  maxPrice: null,
};

/* ── small helpers ───────────────────────────────────────────────────────── */

const text = (v) => (v == null ? "" : String(v).trim());
const keyOf = (v) => text(v).toLowerCase();

// Colours arrive cased however they were typed: "white", "Blue", "blue",
// "Yellow " with a trailing space. One display form per colour, keyed on the
// lowercase value so those three blues are one chip and not three. Garment
// types ("hoodie", "Hoodie", "HOODIE ") have exactly the same problem.
const titleLabel = (v) => {
  const t = text(v);
  if (!t) return t;
  const lower = t.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const stockOf = (p) => (p && Array.isArray(p.stock) ? p.stock : []);

const money = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  try {
    return "₹" + num.toLocaleString("en-IN");
  } catch (e) {
    return "₹" + Math.round(num);
  }
};

/* ── gender ──────────────────────────────────────────────────────────────
   The shopper is offered Men and Women, and nothing else. "Unisex" is not a
   thing anyone shops for; it is a property that makes a garment belong in
   BOTH lists.

   A product with no gender at all is treated the same way. Absent means "the
   owner has not said", not "this garment is for nobody" — hiding stock from
   half the shop because a field has not been filled in yet would quietly
   shrink the catalogue. So: only an explicit "men" or "women" narrows a
   product; everything else matches whichever of the two is asked for. */
const GENDER_OPTIONS = [
  { key: "men", label: "Men" },
  { key: "women", label: "Women" },
];

const isGendered = (v) => {
  const k = keyOf(v);
  return k === "men" || k === "women";
};

/* Sizes are a mix of letter sizes (XS…XXL) and waist numbers (28…46) on this
   catalogue. This is a SORT ORDER, not a list of options — an unknown size
   still shows up, it just sorts after the ones we know how to rank. */
const SIZE_ORDER = [
  "xxxs", "xxs", "xs", "s", "m", "l", "xl",
  "2xl", "xxl", "3xl", "xxxl", "4xl", "xxxxl",
  "free", "free size", "one size", "onesize",
];

const sizeRank = (k) => {
  const i = SIZE_ORDER.indexOf(k);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
};

const compareSizes = (a, b) => {
  const na = Number(a.key);
  const nb = Number(b.key);
  const aNum = a.key !== "" && Number.isFinite(na);
  const bNum = b.key !== "" && Number.isFinite(nb);
  if (aNum && bNum) return na - nb;
  if (aNum !== bNum) return aNum ? 1 : -1;   // letter sizes first, numbers after
  const ra = sizeRank(a.key);
  const rb = sizeRank(b.key);
  if (ra !== rb) return ra - rb;
  return a.label.localeCompare(b.label);
};

// Collects unique {key,label} pairs, first spelling seen wins the label.
const collector = () => {
  const map = new Map();
  return {
    add(raw, makeLabel) {
      const k = keyOf(raw);
      if (!k) return;
      if (!map.has(k)) map.set(k, { key: k, label: makeLabel ? makeLabel(raw) : text(raw) });
    },
    values: () => Array.from(map.values()),
  };
};

/* A ladder of round rupee amounts. Only the rungs that actually fall inside
   this catalogue's price range are offered, so it cannot go stale the way a
   type list would — and it guarantees the owner's "under ₹1000" is one tap
   whenever the shop sells anything on both sides of ₹1000. */
const PRICE_LADDER = [250, 500, 750, 1000, 1500, 2000, 2500, 3000, 4000, 5000, 7500, 10000, 15000, 20000];
const MAX_PRICE_CHIPS = 4;

const priceChips = (min, max) => {
  const inRange = PRICE_LADDER.filter((v) => v > min && v < max);
  if (inRange.length <= MAX_PRICE_CHIPS) return inRange;

  const picked = new Set();
  if (inRange.indexOf(1000) !== -1) picked.add(1000);   // the owner's own example
  const need = MAX_PRICE_CHIPS - picked.size;
  for (let i = 1; i <= need; i += 1) {
    picked.add(inRange[Math.round((i * (inRange.length - 1)) / (need + 1))]);
  }
  return Array.from(picked).sort((a, b) => a - b).slice(0, MAX_PRICE_CHIPS);
};

/* ── facets ──────────────────────────────────────────────────────────────── */

export const EMPTY_FACETS = {
  genders: [],
  types: [],
  colors: [],
  sizes: [],
  priceMin: 0,
  priceMax: 0,
  priceChips: [],
  hasPrices: false,
};

// A product's garment type. It is NOT stored on the product -- the admin
// Filters page keeps that grouping on the type itself so it can never alter
// catalogue data -- so it is resolved through the map fetched from admin.
// `typeOf` is optional: without it nothing has a type, which is exactly the
// state the site is in until the owner builds his groups.
function typeNameFor(product, typeOf) {
  if (!typeOf || !product || !product._id) return "";
  return typeOf.get(String(product._id)) || "";
}

// The garment groupings, read once from admin.
//
// Two things come back: which types the owner has switched ON, and which
// products sit in each. Both live on the garment type itself -- never as a
// field on the product -- because the owner keeps that list separate from his
// catalogue so nothing he does there can change real product data.
//
// Exported so the listing page and the panel share ONE fetch and can never
// disagree about which product is in which type.
//
// null means "not known yet" -- the request has not answered, or failed. In
// that state no type is offered and nothing is filtered out, so the page
// behaves exactly as it did before any of this existed. A dead admin endpoint
// must never empty the shop.
export function useGarmentTypes() {
  const [activeTypes, setActiveTypes] = useState(null);
  const [typeByProduct, setTypeByProduct] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch("https://admin.riymay.com/api/v1/admin/get/all/garment-types?activeOnly=true")
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        const list = Array.isArray(d && d.response) ? d.response : null;
        if (!list) return;

        setActiveTypes(
          list
            .map((t) => String(t && t.name ? t.name : "").trim().toLowerCase())
            .filter(Boolean)
        );

        const map = new Map();
        list.forEach((t) => {
          const name = String(t && t.name ? t.name : "").trim();
          if (!name) return;
          (Array.isArray(t.products) ? t.products : []).forEach((pid) => {
            if (pid) map.set(String(pid), name);
          });
        });
        setTypeByProduct(map);
      })
      .catch(() => { /* stay unknown -- show the page as it was */ });
    return () => { cancelled = true; };
  }, []);

  return { activeTypes, typeByProduct };
}

export function deriveFacets(products, typeOf) {
  const list = Array.isArray(products) ? products : [];

  const types = collector();
  const colors = collector();
  const sizes = collector();
  let anyGendered = false;
  let min = Infinity;
  let max = -Infinity;

  list.forEach((p) => {
    if (!p) return;

    // Garment type: read off the products, never listed here. Nothing in
    // stock of a type ⇒ no chip for it.
    types.add(typeNameFor(p, typeOf), titleLabel);

    if (isGendered(p.gender)) anyGendered = true;

    stockOf(p).forEach((entry) => {
      if (!entry) return;
      colors.add(entry.color, titleLabel);
      sizes.add(entry.size, (v) => text(v).toUpperCase());
    });

    const price = Number(p.price);
    if (Number.isFinite(price)) {
      if (price < min) min = price;
      if (price > max) max = price;
    }
  });

  const hasPrices = Number.isFinite(min) && Number.isFinite(max) && max > min;
  const priceMin = hasPrices ? Math.floor(min) : 0;
  const priceMax = hasPrices ? Math.ceil(max) : 0;

  /* Men/Women is offered only once at least one product actually says "men"
     or "women". While the field is unset — which is every product today — and
     while a catalogue is entirely unisex, both options would match every
     piece, so the facet would be two chips that do nothing. */
  return {
    genders: anyGendered ? GENDER_OPTIONS.slice() : [],
    types: types.values().sort((a, b) => a.label.localeCompare(b.label)),
    colors: colors.values().sort((a, b) => a.label.localeCompare(b.label)),
    sizes: sizes.values().sort(compareSizes),
    priceMin,
    priceMax,
    priceChips: hasPrices ? priceChips(priceMin, priceMax) : [],
    hasPrices,
  };
}

/* ── matching ────────────────────────────────────────────────────────────── */

function matchesSelection(product, sel) {
  if (!product) return false;

  if (sel.genders.length) {
    const g = keyOf(product.gender);
    // Explicitly men or women: must be one of the chosen. Unisex, blank,
    // missing or anything unrecognised: belongs to both, so it always passes.
    if (isGendered(g) && sel.genders.indexOf(g) === -1) return false;
  }

  if (sel.types.length && sel.types.indexOf(keyOf(typeNameFor(product, typeOf))) === -1) {
    return false;
  }

  if (sel.maxPrice != null) {
    const price = Number(product.price);
    if (!Number.isFinite(price) || price > sel.maxPrice) return false;
  }

  const wantSize = sel.sizes.length > 0;
  const wantColor = sel.colors.length > 0;

  if (wantSize || wantColor) {
    // Size AND colour must be satisfiable by the SAME variant. Asking for
    // "M" and "rust" should surface pieces that exist in rust in an M, not a
    // piece that happens to come in an M and, separately, in rust.
    const ok = stockOf(product).some((entry) => {
      if (!entry) return false;
      if (wantSize && sel.sizes.indexOf(keyOf(entry.size)) === -1) return false;
      if (wantColor && sel.colors.indexOf(keyOf(entry.color)) === -1) return false;
      return true;
    });
    if (!ok) return false;
  }

  return true;
}

/**
 * Never throws. On any unexpected shape it returns the products untouched,
 * which is the only acceptable failure mode for a storefront listing.
 */
export function applyFilters(products, selection, typeOf) {
  const list = Array.isArray(products) ? products : [];
  try {
    const sel = { ...EMPTY_SELECTION, ...(selection || {}) };
    if (
      !sel.genders.length &&
      !sel.types.length &&
      !sel.colors.length &&
      !sel.sizes.length &&
      sel.maxPrice == null
    ) {
      return list;
    }
    return list.filter((p) => matchesSelection(p, sel));
  } catch (err) {
    console.error("Product filters failed, showing every product:", err);
    return list;
  }
}

export const countActive = (selection) => {
  const sel = { ...EMPTY_SELECTION, ...(selection || {}) };
  return (
    sel.genders.length +
    sel.types.length +
    sel.colors.length +
    sel.sizes.length +
    (sel.maxPrice == null ? 0 : 1)
  );
};

/* ── UI ──────────────────────────────────────────────────────────────────── */

const MOBILE_QUERY = "(max-width: 768px)";

const readIsMobile = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(MOBILE_QUERY).matches;
};

function ProductFilters({ products, selection, onChange, resultCount }) {
  /* One pattern at every width: a "Filters" button that opens the panel, and
     a panel that is CLOSED until it is asked for. On a phone the panel is a
     bottom sheet; on a desktop it is a drop-down anchored under the button.
     Same component, same markup, same state — the two cannot drift. */
  const [open, setOpen] = useState(false);
  // Only under the mobile breakpoint is this panel a modal dialog. On desktop
  // it is a non-modal drop-down, and announcing it as a modal — or trapping
  // Tab inside it, or locking the page's scroll — would be a lie.
  const [isMobile, setIsMobile] = useState(readIsMobile);
  const closeRef = useRef(null);
  const triggerRef = useRef(null);
  const sheetRef = useRef(null);
  const rootRef = useRef(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChangeMq = () => setIsMobile(mq.matches);
    onChangeMq();
    if (mq.addEventListener) mq.addEventListener("change", onChangeMq);
    else if (mq.addListener) mq.addListener(onChangeMq);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChangeMq);
      else if (mq.removeListener) mq.removeListener(onChangeMq);
    };
  }, []);

  // The garment types the owner has switched ON in admin.
  //
  // The options themselves still come from the products, so a type with
  // nothing in stock disappears on its own. This list is only ever used to
  // take options AWAY: a type he has hidden for the season stays hidden even
  // while stock of it exists.
  //
  // null means "we do not know" -- the request has not answered, or failed.
  // In that state nothing is filtered out, so the panel behaves exactly as it
  // would without this at all. A dead admin endpoint must never empty the
  // filters on the storefront.
  const { activeTypes, typeByProduct } = useGarmentTypes();

  const all = Array.isArray(products) ? products : [];
  const facets = useMemo(() => {
    try {
      const derived = deriveFacets(all, typeByProduct);
      if (!Array.isArray(activeTypes)) return derived;
      const allowed = new Set(activeTypes);
      const types = derived.types.filter((t) =>
        allowed.has(String(t.label || t.key || "").trim().toLowerCase())
      );
      // One option is not a choice -- same rule the other facets use.
      return { ...derived, types: types.length >= 2 ? types : [] };
    } catch (err) {
      console.error("Could not read filter options from the products:", err);
      return { ...EMPTY_FACETS };
    }
  }, [all, activeTypes, typeByProduct]);

  const activeCount = countActive(selection);

  /* How many products each option would return, measured against the OTHER
     active filters. A zero means picking it leads nowhere, so it is disabled —
     the fastest way to make a dead-end combination impossible to reach. */
  const counts = useMemo(() => {
    const countWith = (patch) => applyFilters(all, { ...selection, ...patch }, typeByProduct).length;
    const out = { genders: {}, types: {}, colors: {}, sizes: {}, price: {} };
    facets.genders.forEach((o) => { out.genders[o.key] = countWith({ genders: [o.key] }); });
    facets.types.forEach((o) => { out.types[o.key] = countWith({ types: [o.key] }); });
    facets.colors.forEach((o) => { out.colors[o.key] = countWith({ colors: [o.key] }); });
    facets.sizes.forEach((o) => { out.sizes[o.key] = countWith({ sizes: [o.key] }); });
    facets.priceChips.forEach((v) => { out.price[v] = countWith({ maxPrice: v }); });
    return out;
  }, [all, facets, selection]);

  const toggle = (group, key) => {
    const current = selection[group] || [];
    const next = current.indexOf(key) === -1
      ? current.concat(key)
      : current.filter((k) => k !== key);
    onChange({ ...selection, [group]: next });
  };

  const setMaxPrice = (value) => onChange({ ...selection, maxPrice: value });
  const clearAll = () => onChange({ ...EMPTY_SELECTION });

  /* Escape closes at every width. Tab is only trapped while the panel is a
     modal sheet on a phone; on desktop Tab walks out of the drop-down the way
     it walks out of any other block, and leaving closes it. */
  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (!isMobile) return;
      if (e.key !== "Tab" || !sheetRef.current) return;
      const focusable = sheetRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!sheetRef.current.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    // Desktop drop-down: a click or a focus anywhere else dismisses it. The
    // phone sheet has its scrim for that.
    const onAway = (e) => {
      if (isMobile || !rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };

    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onAway);
    document.addEventListener("focusin", onAway);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onAway);
      document.removeEventListener("focusin", onAway);
    };
  }, [open, isMobile]);

  // Scroll lock, for the bottom sheet only — a desktop drop-down that froze
  // the page behind it would be broken. Restores whatever was there before.
  useEffect(() => {
    if (!open || !isMobile) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [open, isMobile]);

  /* Focus moves into the panel when it opens, and comes back to the button
     that opened it when it closes. Without the second half, focus is left
     sitting on a control inside a `visibility: hidden` panel and the next Tab
     starts from nowhere. */
  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      if (closeRef.current) closeRef.current.focus();
      return;
    }
    // First render: the panel has never been open, so there is no focus to
    // give back. Grabbing it here would yank focus on page load.
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;

    const active = document.activeElement;
    const lost = !active || active === document.body ||
      (sheetRef.current && sheetRef.current.contains(active));
    if (lost && triggerRef.current) triggerRef.current.focus();
  }, [open]);

  /* Men / Women first, then the garment, then how it looks, then what it
     costs. Gender and type disappear entirely until the owner starts filling
     those fields in. A facet with a single option is not a choice, so it is
     dropped — except gender, which is always the pair or nothing. */
  const groups = [
    { id: "genders", title: "Shop", options: facets.genders, counts: counts.genders, min: 2 },
    // Shirt, Hoodie, Shacket. Deliberately NOT called categories: the
    // collection names (The Shirt Atelier, Denim Form) are a different thing
    // entirely, and using the same word for both is how they get merged by
    // mistake later.
    { id: "types", title: "Type", options: facets.types, counts: counts.types, min: 2 },
    { id: "colors", title: "Colour", options: facets.colors, counts: counts.colors, min: 2 },
    { id: "sizes", title: "Size", options: facets.sizes, counts: counts.sizes, min: 2 },
  ].filter((g) => g.options.length >= g.min);

  const showPrice = facets.hasPrices;

  // Nothing worth choosing between — don't put a filter bar on the page.
  if (!groups.length && !showPrice) return null;

  const labelFor = (group, key) => {
    const found = (facets[group] || []).find((o) => o.key === key);
    return found ? found.label : key;
  };

  const activeChips = []
    .concat((selection.genders || []).map((k) => ({ id: "g" + k, label: labelFor("genders", k), remove: () => toggle("genders", k) })))
    .concat((selection.types || []).map((k) => ({ id: "t" + k, label: labelFor("types", k), remove: () => toggle("types", k) })))
    .concat((selection.colors || []).map((k) => ({ id: "k" + k, label: labelFor("colors", k), remove: () => toggle("colors", k) })))
    .concat((selection.sizes || []).map((k) => ({ id: "s" + k, label: "Size " + labelFor("sizes", k), remove: () => toggle("sizes", k) })))
    .concat(selection.maxPrice == null ? [] : [{ id: "price", label: "Under " + money(selection.maxPrice), remove: () => setMaxPrice(null) }]);

  const sliderValue = selection.maxPrice == null ? facets.priceMax : selection.maxPrice;
  const sliderStep = facets.priceMax - facets.priceMin > 2000 ? 100 : 50;

  return (
    <section className="pf" aria-label="Filter products" ref={rootRef}>
      <div className="pf-bar">
        <p className="pf-count" aria-live="polite">
          {resultCount} {resultCount === 1 ? "piece" : "pieces"}
          {activeCount > 0 && all.length !== resultCount ? " of " + all.length : ""}
        </p>

        {activeCount > 0 && (
          <button type="button" className="pf-clear" onClick={clearAll}>
            Clear all
          </button>
        )}

        {/* Top right of the listing at every width. See .pf-bar in the CSS for
            how it keeps clear of the fixed floating logo mark in the corner. */}
        <button
          type="button"
          className="pf-trigger"
          ref={triggerRef}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="pf-sheet"
        >
          Filters
          {activeCount > 0 && <span className="pf-badge">{activeCount}</span>}
        </button>
      </div>

      {activeChips.length > 0 && (
        <ul className="pf-active">
          {activeChips.map((chip) => (
            <li key={chip.id}>
              <button
                type="button"
                className="pf-active-chip"
                onClick={chip.remove}
                aria-label={"Remove filter " + chip.label}
              >
                <span>{chip.label}</span>
                <span className="pf-x" aria-hidden="true">&times;</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* One element for both layouts: a drop-down under the button above
          769px, a bottom sheet below it. No second copy of the markup to
          drift, and neither shape is in the grid's flow — the product grid is
          laid out exactly as it would be with no filters at all. */}
      <div
        className={"pf-scrim" + (open ? " is-open" : "")}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      <div
        id="pf-sheet"
        ref={sheetRef}
        className={"pf-sheet" + (open ? " is-open" : "")}
        role={isMobile ? "dialog" : "group"}
        aria-modal={isMobile && open ? "true" : undefined}
        aria-label="Filters"
      >
        <div className="pf-sheet-head">
          <h2 className="pf-sheet-title">Filters</h2>
          <button type="button" className="pf-close" ref={closeRef} onClick={() => setOpen(false)}>
            Close
          </button>
        </div>

        <div className="pf-groups">
          {groups.map((group) => (
            <div className={"pf-group pf-group-" + group.id} key={group.id}>
              <h3 className="pf-group-title">{group.title}</h3>
              <ul className="pf-options">
                {group.options.map((option) => {
                  const on = (selection[group.id] || []).indexOf(option.key) !== -1;
                  const count = group.counts[option.key] || 0;
                  return (
                    <li key={option.key}>
                      <button
                        type="button"
                        className={"pf-chip" + (on ? " is-on" : "")}
                        aria-pressed={on}
                        disabled={!on && count === 0}
                        onClick={() => toggle(group.id, option.key)}
                      >
                        <span className="pf-chip-label">{option.label}</span>
                        <span className="pf-chip-count">{count}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {showPrice && (
            <div className="pf-group pf-group-price">
              <h3 className="pf-group-title">Price</h3>
              <ul className="pf-options">
                {facets.priceChips.map((value) => {
                  const on = selection.maxPrice === value;
                  const count = counts.price[value] || 0;
                  return (
                    <li key={value}>
                      <button
                        type="button"
                        className={"pf-chip" + (on ? " is-on" : "")}
                        aria-pressed={on}
                        disabled={!on && count === 0}
                        onClick={() => setMaxPrice(on ? null : value)}
                      >
                        <span className="pf-chip-label">Under {money(value)}</span>
                        <span className="pf-chip-count">{count}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <label className="pf-range-label" htmlFor="pf-price-range">
                Up to <strong>{money(sliderValue)}</strong>
              </label>
              <input
                id="pf-price-range"
                className="pf-range"
                type="range"
                min={facets.priceMin}
                max={facets.priceMax}
                step={sliderStep}
                value={sliderValue}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  setMaxPrice(next >= facets.priceMax ? null : next);
                }}
              />
              <div className="pf-range-ends" aria-hidden="true">
                <span>{money(facets.priceMin)}</span>
                <span>{money(facets.priceMax)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="pf-sheet-foot">
          <button type="button" className="pf-foot-clear" onClick={clearAll} disabled={activeCount === 0}>
            Clear all
          </button>
          <button type="button" className="pf-foot-apply" onClick={() => setOpen(false)}>
            Show {resultCount} {resultCount === 1 ? "piece" : "pieces"}
          </button>
        </div>
      </div>
    </section>
  );
}

export default ProductFilters;
